import { useState } from "react";
import { TrendingUp, Zap, Timer } from "lucide-react";
import { Eyebrow, Reveal } from "./Reveal";

const fmt = (n) => "€" + Math.round(n).toLocaleString("en-US");

export default function ROICalculator() {
  const [leads, setLeads] = useState(40);
  const [deal, setDeal] = useState(7500);

  const extraDeals = Math.round(leads * 12 * 0.3);
  const extraPipeline = extraDeals * deal;

  return (
    <section className="relative mx-auto max-w-[1440px] px-6 py-24 md:px-10 md:py-32" data-testid="roi-calculator-section">
      <Reveal>
        <Eyebrow>Modeled on real deployments</Eyebrow>
        <h2 className="mt-4 font-display text-3xl font-extrabold tracking-tight sm:text-4xl lg:text-5xl">
          Pipeline impact <span className="text-gradient">calculator</span>
        </h2>
        <p className="mt-4 max-w-xl text-sm leading-relaxed text-slate-500 sm:text-base">
          Drag the sliders to model what Vector's funnel and automation systems could add to
          your pipeline — based on the 30% pipeline uplift and 22% faster lead-to-opportunity
          conversion achieved in client deployments.
        </p>
      </Reveal>

      <Reveal delay={0.1}>
        <div className="mt-12 grid gap-px overflow-hidden rounded-2xl border hairline bg-azure/10 lg:grid-cols-2">
          <div className="bg-surface/80 p-8 md:p-10">
            <div className="space-y-10">
              <div>
                <div className="flex items-baseline justify-between">
                  <label htmlFor="roi-leads" className="font-mono text-[11px] uppercase tracking-[0.24em] text-slate-500">
                    Monthly inbound leads
                  </label>
                  <span className="font-display text-2xl font-bold text-neon" data-testid="roi-leads-value">{leads}</span>
                </div>
                <input
                  id="roi-leads"
                  type="range"
                  min="10"
                  max="500"
                  step="5"
                  value={leads}
                  onChange={(e) => setLeads(Number(e.target.value))}
                  className="mt-5"
                  data-testid="roi-leads-slider"
                />
              </div>
              <div>
                <div className="flex items-baseline justify-between">
                  <label htmlFor="roi-deal" className="font-mono text-[11px] uppercase tracking-[0.24em] text-slate-500">
                    Average deal value
                  </label>
                  <span className="font-display text-2xl font-bold text-neon" data-testid="roi-deal-value">{fmt(deal)}</span>
                </div>
                <input
                  id="roi-deal"
                  type="range"
                  min="500"
                  max="50000"
                  step="500"
                  value={deal}
                  onChange={(e) => setDeal(Number(e.target.value))}
                  className="mt-5"
                  data-testid="roi-deal-slider"
                />
              </div>
            </div>
          </div>

          <div className="bg-ink/80 p-8 md:p-10">
            <div className="grid gap-4 sm:grid-cols-2">
              <div className="rounded-xl border hairline bg-elevated/60 p-5">
                <TrendingUp className="h-5 w-5 text-neon" />
                <p className="mt-4 font-display text-3xl font-extrabold text-slate-100" data-testid="roi-result-opportunities">+{extraDeals}</p>
                <p className="mt-1 text-xs leading-relaxed text-slate-500">additional opportunities per year at a 30% pipeline uplift</p>
              </div>
              <div className="rounded-xl border hairline bg-elevated/60 p-5">
                <Zap className="h-5 w-5 text-neon" />
                <p className="mt-4 font-display text-3xl font-extrabold text-slate-100" data-testid="roi-result-pipeline">{fmt(extraPipeline)}</p>
                <p className="mt-1 text-xs leading-relaxed text-slate-500">projected incremental pipeline value per year</p>
              </div>
              <div className="rounded-xl border hairline bg-elevated/60 p-5 sm:col-span-2">
                <Timer className="h-5 w-5 text-neon" />
                <p className="mt-4 font-display text-3xl font-extrabold text-slate-100">22% faster</p>
                <p className="mt-1 text-xs leading-relaxed text-slate-500">lead-to-opportunity cycle with AI-assisted Salesforce CRM automation</p>
              </div>
            </div>
            <p className="mt-6 font-mono text-[10px] uppercase tracking-[0.2em] text-slate-600">
              Indicative model — not a guarantee of results
            </p>
          </div>
        </div>
      </Reveal>
    </section>
  );
}
